import * as Sentry from "@sentry/react"
import ReactDOM from "react-dom/client"
import Providers from "./providers"
import "@/shared/lib/react-i18next"
import React from "react"
import { isDev } from "@/shared/lib/env/env"
import {
  applyEnvironmentFavicon,
  environmentCue,
} from "@/shared/lib/env/environment"
import { stashAuthHandoffFromHash } from "@/shared/lib/auth/handoff"
import { rememberReturnPathOnLoad } from "@/shared/lib/auth/return-path"
import { stashAnonymousOrderTokenFromHash } from "@/entities/project/anonymous-order-token"
import { useAuthStore } from "@/entities/auth/auth.model"

const rootElement = document.getElementById("app")!

// Move any SSO / marketplace login hand-off tokens out of the URL fragment at the
// very top of bootstrap, before anything else can read it. Browser tracing and
// replay -- which recorded URLs and were the original reason for the ordering --
// are gone, but the fragment still sits in the address bar and in history, so a
// screenshot, a shared link or an extension would carry live JWTs. The hand-off
// routes read them via takeAuthHandoff.
stashAuthHandoffFromHash()
// The same for an anonymous order link's token (`/orders/anonymous/{id}#token=...`):
// an error reported while the app or the route loads would otherwise carry it.
stashAnonymousOrderTokenFromHash()
// A signed-out visitor's deep link, recorded before any route guard sends them to the login form
// and read back by the sign-in. After the two stashes above, so a token fragment is never part of
// what is recorded.
rememberReturnPathOnLoad({
  isAuthenticated: useAuthStore.getState().isAuthenticated,
})

applyEnvironmentFavicon(environmentCue)

if (!isDev) {
  // Errors only. Browser tracing and session replay were both on and neither
  // earned its keep: tracing produced roughly a quarter of a million spans a
  // month whose only visible output was two performance-detector warnings with
  // zero users, and replay sat pinned at its cap with hundreds of sessions
  // dropped. Removing the integrations rather than zeroing their sample rates
  // also keeps them out of the bundle. Neither is available on the plan this org
  // is moving to, so this is what would have happened anyway, deliberately.
  //
  // `sendDefaultPii` stays: it is what attaches the affected user to an issue,
  // and the reason it was a concern -- tracing and replay recording URLs -- is
  // what just went away.
  Sentry.init({
    dsn: "https://b92fa63c1b75f8f8484a66046759f234@o1418486.ingest.us.sentry.io/4510241813954560",
    sendDefaultPii: true,
    // Injected by Outlook's SafeLink crawler, not by this app: it drives the page
    // headlessly and its own script fails. Two issues, both unactionable, and no
    // amount of fixing anything here changes them.
    ignoreErrors: ["Object Not Found Matching Id"],
  })
}

if (!rootElement.innerHTML) {
  const root = ReactDOM.createRoot(rootElement)
  root.render(
    <React.StrictMode>
      <Providers />
    </React.StrictMode>
  )
}
